import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { EditIcon } from "vega-icons";

import { type AssetClass } from "@vega-api/generated/types/assetClass";
import { type CasTemplate } from "@vega-api/generated/types/casTemplate";
import { TableActionMenu } from "@vega-core/components/ActionMenu/TableActionMenu";
import {
  ACTION_COLUMN_DEFAULT_PROPS,
  MAX_TEXT_COLUMN_PROPS,
} from "@vega-core/components/Table/CoreTable/constants";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { FundLogos } from "@vega-modules/components/AssetClassLogos/AssetClassImages";

export type FundCASTemplate = {
  id: string;
  fundName: string;
  assetClass?: AssetClass;
  template: CasTemplate;
  lpPortalVisibility: boolean;
};

type UseCASTableColumnsProps = {
  onEditVisibility?: (fund: FundCASTemplate) => void;
};

export const useCASTableColumns = ({
  onEditVisibility,
}: UseCASTableColumnsProps = {}): ColumnDef<FundCASTemplate>[] =>
  useMemo(
    () => [
      {
        accessorKey: "fund",
        header: m.settings_casManagement_table_fund(),
        ...MAX_TEXT_COLUMN_PROPS,
        cell: ({ row }) => {
          const { assetClass, fundName } = row.original;

          return (
            <div className="flex items-center gap-3">
              <FundLogos assetClass={assetClass} />
              <span className="body-base-medium text-text/primary">
                {fundName}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "template",
        header: m.settings_casManagement_table_template(),
        ...MAX_TEXT_COLUMN_PROPS,
        cell: ({ row }) => {
          const { template } = row.original;

          return (
            <div className="body-base-regular text-text/primary">
              {template.templateName || "-"}
            </div>
          );
        },
      },
      {
        accessorKey: "lpPortalVisibility",
        header: m.settings_casManagement_table_lpPortalVisibility(),
        ...MAX_TEXT_COLUMN_PROPS,
        cell: ({ row }) => {
          const { lpPortalVisibility } = row.original;

          return (
            <Tag
              dataTestId={`visibility-tag-${row.original.id}`}
              variant={
                lpPortalVisibility ? TagVariants.Positive : TagVariants.Default
              }
            >
              {lpPortalVisibility
                ? m.settings_casManagement_table_active()
                : m.settings_casManagement_table_inactive()}
            </Tag>
          );
        },
      },
      {
        ...ACTION_COLUMN_DEFAULT_PROPS,
        cell: ({ row }) => {
          const fund = row.original;

          return (
            <TableActionMenu
              dataTestId={`cas-template-actions-${fund.id}`}
              menuItems={[
                {
                  title: m.cas_action_edit_visibility(),
                  onClick: () => onEditVisibility?.(fund),
                  icon: <EditIcon />,
                },
              ]}
              withBorders={false}
            />
          );
        },
      },
    ],
    [onEditVisibility],
  );
